/**
 * 余额变动 — Node + PHP 合并只读列表（仪表盘入口）。
 */
import { useMemo, useState } from 'react';
import { Button, DatePicker, Input, Select, Space, Tag, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { DataTable } from '../../components/DataTable';
import { TOKEN_IDS, type TokenId } from '@uo/shared';

interface BalanceChangeRow {
  id: string;
  source: 'node' | 'php';
  user_id: number;
  player_number: string | null;
  username: string | null;
  asset: string;
  action: string;
  delta: string;
  balance_before: string;
  balance_after: string;
  note: string | null;
  ref: string | null;
  create_time: string;
}

export function BalanceChangesPage() {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [asset, setAsset] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day').startOf('day'),
    dayjs().endOf('day'),
  ]);

  const extraParams = useMemo(
    () => ({
      q: q || undefined,
      asset,
      start_date: range[0]?.format('YYYY-MM-DD'),
      end_date: range[1]?.format('YYYY-MM-DD'),
    }),
    [q, asset, range],
  );

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        余额变动
      </Typography.Title>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        合并 Node 账本与 PHP 镜像流水；同笔优先显示 Node。默认近 7 天。
      </Typography.Text>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          placeholder="玩家号 / 账号 / 用户名 / ID"
          style={{ width: 240 }}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => setQ(search.trim())}
        />
        <Select
          placeholder="币种"
          style={{ width: 140 }}
          allowClear
          value={asset}
          onChange={setAsset}
          options={TOKEN_IDS.map((t: TokenId) => ({ value: t, label: t.toUpperCase() }))}
        />
        <DatePicker.RangePicker
          value={range}
          onChange={(v) => {
            if (v?.[0] && v?.[1]) setRange([v[0], v[1]]);
          }}
          allowClear={false}
        />
        <Button
          type="primary"
          onClick={() => {
            setQ(search.trim());
          }}
        >
          搜索
        </Button>
      </Space>

      <DataTable<BalanceChangeRow>
        queryKey={['admin', 'balance-changes', q, asset ?? '', range[0].valueOf(), range[1].valueOf()]}
        endpoint="/api/admin/balance-changes"
        extraParams={extraParams}
        columns={[
          {
            title: '时间',
            dataIndex: 'create_time',
            width: 170,
            render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
          },
          {
            title: '玩家号',
            dataIndex: 'player_number',
            width: 100,
            render: (v: string | null, r) => v ?? r.user_id,
          },
          {
            title: '账号',
            dataIndex: 'username',
            width: 140,
            ellipsis: true,
            render: (v: string | null) => v ?? '—',
          },
          {
            title: '币种',
            dataIndex: 'asset',
            width: 80,
            render: (v: string) => v.toUpperCase(),
          },
          {
            title: '变动',
            dataIndex: 'delta',
            width: 140,
            render: (v: string) => (
              <span
                style={{
                  color: String(v).startsWith('-') ? '#ff4d4f' : '#52c41a',
                  fontFamily: 'monospace',
                }}
              >
                {v}
              </span>
            ),
          },
          { title: '变动前', dataIndex: 'balance_before', width: 140 },
          { title: '变动后', dataIndex: 'balance_after', width: 140 },
          {
            title: '备注',
            dataIndex: 'note',
            ellipsis: true,
            render: (v: string | null) => v ?? '—',
          },
          {
            title: '来源',
            dataIndex: 'source',
            width: 80,
            render: (v: BalanceChangeRow['source']) =>
              v === 'node' ? <Tag color="blue">Node</Tag> : <Tag color="default">PHP</Tag>,
          },
          {
            title: '动作',
            dataIndex: 'action',
            width: 140,
            ellipsis: true,
          },
        ]}
        rowKey="id"
        scroll={{ x: 1400 }}
      />
    </div>
  );
}
